import { jsx as _jsx } from "react/jsx-runtime";
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import styled from '@emotion/styled';
import moment from 'moment';
import { colors, defaultFontFamily as defaultFont, } from '@modernhealth/vital-foundation';
import { pxToRem } from '../../helpers/pxToRem';
import { ColorVariant } from './ColorVariant';
import { DatePicker } from './DatePicker';
import { InputField } from './Field';
// StyledInput
const StyledInput = styled.input(({ variant }) => {
    let borderColor = '';
    switch (variant) {
        case ColorVariant.Error:
            borderColor = colors.red55;
            break;
        case ColorVariant.Active:
            borderColor = colors.violet55;
            break;
        default:
            borderColor = colors.gray10;
    }
    return {
        width: '100%',
        borderRadius: pxToRem(4),
        padding: pxToRem(12),
        borderStyle: 'solid',
        borderWidth: pxToRem(2),
        borderColor,
        color: colors.gray85,
        fontFamily: defaultFont,
        fontWeight: 400,
        fontSize: pxToRem(16),
        lineHeight: pxToRem(24),
        outline: 'none',
        ':focus': {
            borderColor,
        },
        '&::placeholder': {
            color: colors.gray55,
        },
    };
});
// Textfield
export const TextField = styled.textarea(({ variant, height = 150 }) => {
    let borderColor = '';
    switch (variant) {
        case ColorVariant.Error:
            borderColor = colors.red55;
            break;
        case ColorVariant.Active:
            borderColor = colors.violet55;
            break;
        default:
            borderColor = colors.gray10;
    }
    return {
        width: '100%',
        height: pxToRem(height),
        padding: pxToRem(12),
        borderRadius: pxToRem(4),
        borderStyle: 'solid',
        borderWidth: pxToRem(2),
        borderColor,
        color: colors.gray85,
        fontFamily: defaultFont,
        fontWeight: 400,
        fontSize: pxToRem(16),
        lineHeight: pxToRem(24),
        outline: 'none',
        ':focus': {
            borderColor,
        },
        '&::placeholder': {
            color: colors.gray55,
        },
    };
});
// Main input component
export var InputType;
(function (InputType) {
    InputType["DatePicker"] = "date-picker";
    InputType["TextField"] = "text";
    InputType["TextArea"] = "text-area";
    InputType["Password"] = "password";
})(InputType || (InputType = {}));
var ComponentNames;
(function (ComponentNames) {
    ComponentNames["TextField"] = "TextField";
    ComponentNames["Input"] = "Input";
})(ComponentNames || (ComponentNames = {}));
/**
 * @deprecated
 */
export const Input = ({ fieldId, type, value, initialValue, useDefaultPlaceholder, onChange, onBlur, onFocus, label, helpText = '', error = '', testID = '', readOnly, height, }) => {
    const minDate = moment().subtract(90, 'years');
    const maxDate = moment();
    // Duet Date Picker's icon button is 48px wide
    const rightPadding = type === InputType.DatePicker ? '48px' : '0';
    return (_jsx(InputField, { fieldId: fieldId, label: label, error: error, helpText: helpText, value: value, initialValue: initialValue, rightPadding: rightPadding, useDefaultPlaceholder: useDefaultPlaceholder, renderInput: state => {
            const { onFocus: vOnFocus, onUnfocus: vOnUnfocus, setIsFilled, setIsFloatingLabelFixed, variant: colorVariant, labelId, helpId, } = state;
            const inputOnBlur = (e) => {
                vOnUnfocus();
                onBlur?.(e);
            };
            const inputOnFocus = (e) => {
                vOnFocus();
                onFocus?.(e);
            };
            const handleChange = (changedValue) => {
                setIsFloatingLabelFixed(false);
                setIsFilled(!!changedValue);
                onChange?.(changedValue);
            };
            switch (type) {
                case InputType.DatePicker:
                    if (value !== undefined) {
                        throw new Error('<Input /> type "DatePicker" does not support "value". Use "inputValue".');
                    }
                    return (_jsx(DatePicker, { id: fieldId, hasError: !!error, "aria-labelledby": labelId, name: "date", initialValue: initialValue ? moment(initialValue, 'YYYY-MM-DD') : undefined, 
                        // @ts-expect-error These are typed as HTML handlers
                        onBlur: inputOnBlur, 
                        // @ts-expect-error These are typed as HTML handlers
                        onFocus: inputOnFocus, minMoment: minDate, maxMoment: maxDate, onChange: value => {
                            // Date Picker doesn't support value because it is not
                            // a React component.
                            setIsFilled(!!value);
                            handleChange(value?.format('YYYY-MM-DD') ?? '');
                        }, "data-testid": testID }));
                case InputType.TextArea:
                    return (_jsx(TextField, { id: fieldId, "aria-labelledby": useDefaultPlaceholder ? undefined : labelId, "aria-label": useDefaultPlaceholder ? label : undefined, "aria-describedby": error || helpText ? helpId : undefined, "aria-invalid": !!error, name: fieldId, value: value, variant: colorVariant, onChange: e => handleChange(e.target.value), onBlur: inputOnBlur, onFocus: inputOnFocus, "data-testid": testID, "data-component": ComponentNames.TextField, placeholder: useDefaultPlaceholder ? label : undefined, height: height }));
                default:
                    return (_jsx(StyledInput, { "aria-labelledby": useDefaultPlaceholder ? undefined : labelId, "aria-label": useDefaultPlaceholder ? label : undefined, "aria-invalid": !!error, type: type, id: fieldId, name: fieldId, value: value, onBlur: inputOnBlur, onFocus: inputOnFocus, variant: colorVariant, onChange: e => handleChange(e.target.value), readOnly: readOnly, "data-testid": testID, "data-component": ComponentNames.Input, placeholder: useDefaultPlaceholder ? label : undefined }));
            }
        } }));
};
